// What is set up where on a transaction — ONE reading of the model behind
// every index the redesigned designer shows: the form ▾ / Whole record ▾
// menus, the "Set up on …" lists under the form card, the Logic timeline, the
// ⌘K search and the Settings pills. Each entry carries the TreeNode that
// opens it in the inspector, so a click anywhere lands on the same editor.

import type {
  TransactionModel, SqlModel, ColumnDef, TreeNode, ItemChange, Validation, FollowupActionDef,
} from './types'
import {
  normalizeItemChange, validationSummary, isValidationRule, legacyActionToDef,
  normalizeFollowupAction, ACTION_HANDLER_LABELS,
} from './types'
import { allSqlModels, childFormsOf, headerFormNo } from '@twasta/page-runtime/txn/utils'

/** How a piece of logic is written. 'Button' / 'Gate' only on the timeline. */
export type LogicKind = 'Rule' | 'Code' | 'API' | 'Button' | 'Gate'
export type SearchKind = 'Columns' | 'Events' | 'Buttons'

export interface SetupItem {
  id: string
  name: string
  detail: string
  kind: LogicKind
  /** 'record' or `form:<index>` — the Logic workspace's "Applies to" filter. */
  scopeKey: string
  scopeLabel: string
  node: TreeNode
  /** Identifier-like names render in the mono face. */
  mono?: boolean
  /** Lower-cased text the search matches (rule text, SQL, parameters…). */
  haystack: string
  searchKind: SearchKind
}

export interface SetupCategory {
  key: string
  title: string
  /** Short count shown beside the title in menus ("3", "3 tables", "On · 2 levels"). */
  count: string
  items: SetupItem[]
  /** Opens the list in the inspector (where "+ Add" lives). */
  node: TreeNode
}

// ── small readers ──────────────────────────────────────────────────────────

const truthy = (v: unknown) => v === true || v === 'true' || v === '1' || v === 1

export function formTitle(sm: SqlModel | undefined, fi: number): string {
  return (sm?.form_title || '').trim() || `Form ${sm?.form_no || fi + 1}`
}

/** Rule / Code (language) / API for an item_change-shaped value. */
export function logicKindOf(raw: ItemChange | string | null | undefined): { kind: LogicKind; label: string } {
  const ic = normalizeItemChange(raw)
  if (!ic) return { kind: 'Code', label: 'Code' }
  if (ic.mode === 'rule') return { kind: 'Rule', label: 'Rule' }
  if (ic.mode === 'rest_api') return { kind: 'API', label: 'Call API' }
  const lang = ic.mode === 'business_logic' ? (ic.business_logic?.language || '') : ''
  return { kind: 'Code', label: lang ? `Code · ${langLabel(lang)}` : 'Code' }
}

function langLabel(lang: string): string {
  const l = lang.toLowerCase()
  if (l === 'python') return 'Python'
  if (l === 'java') return 'Java'
  if (l === 'plpgsql') return 'plpgsql'
  if (l === 'plsql') return 'PL/SQL'
  return lang
}

/** One line saying what an item_change-shaped value does. */
export function logicDetail(raw: ItemChange | string | null | undefined): string {
  const ic = normalizeItemChange(raw)
  if (!ic) return ''
  if (ic.mode === 'rule') {
    const fills = (ic.fill || []).map(f => f.form_col).filter(Boolean)
    const from = (ic.source_table || '').trim()
    if (fills.length) {
      const head = fills.slice(0, 3).join(', ')
      return `Fills ${head}${fills.length > 3 ? ` +${fills.length - 3}` : ''}${from ? ` from ${from}` : ''}`
    }
    return from ? `From ${from}` : 'Rule'
  }
  if (ic.mode === 'business_logic') return (ic.business_logic?.method || '').trim() || 'Function not named yet'
  if (ic.mode === 'rest_api') return (ic.rest_api?.url || '').trim() || 'No URL yet'
  return 'Code'
}

/** Everything searchable about an item_change-shaped value. */
function logicText(raw: ItemChange | string | null | undefined): string {
  if (raw == null) return ''
  try { return (typeof raw === 'string' ? raw : JSON.stringify(raw)).toLowerCase() } catch { return '' }
}

function validationText(v: Validation): string {
  try { return (typeof v === 'string' ? v : JSON.stringify(v)).toLowerCase() } catch { return '' }
}

function validationKind(v: Validation): LogicKind {
  if (!isValidationRule(v)) return 'Rule'
  if (v.type === 'business_logic') return 'Code'
  if (v.type === 'rest_api') return 'API'
  return 'Rule'
}

function validationName(v: Validation): string {
  if (isValidationRule(v)) return (v.name || '').trim() || validationSummary(v)
  return validationSummary(v)
}

function severityLabel(v: Validation): string {
  if (!isValidationRule(v)) return 'Error'
  return v.severity === 'W' ? 'Warning' : v.severity === 'P' ? 'Prompt' : 'Error'
}

const configured = (raw: unknown) => !!normalizeItemChange(raw as ItemChange | string | null | undefined)

function colLabel(c: ColumnDef): string {
  return c.name || c.db_name || ''
}

function followupEventLabel(event: string): string {
  const e = (event || '').toLowerCase()
  if (e.startsWith('on-action:')) return `after ${event.slice('on-action:'.length)}`
  const parts: string[] = []
  if (e.includes('add')) parts.push('add')
  if (e.includes('edit')) parts.push('edit')
  if (e.includes('delete')) parts.push('delete')
  return parts.length ? `on ${parts.join(' / ')}` : event
}

function followupKind(d: FollowupActionDef): LogicKind {
  // A send is delivered through IOFlow, like an integration event.
  if (d.send) return 'API'
  if (d.ioflow_event) return 'API'
  if (d.rest_api) return 'API'
  return 'Code'
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

// ── per form ───────────────────────────────────────────────────────────────

export function formButtons(model: TransactionModel, fi: number): SetupItem[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  return (sm?.actions || []).map((a, i) => {
    const d = legacyActionToDef(a)
    const name = (d.name || '').trim() || 'Unnamed button'
    const handler = ACTION_HANDLER_LABELS[d.handler] || d.handler
    return {
      id: `fbtn:${fi}:${i}`, name, detail: buttonDetail(d), kind: 'Button' as LogicKind,
      scopeKey: `form:${fi}`, scopeLabel: title,
      node: { type: 'form-actions', formIndex: fi, actionIndex: i, label: name },
      haystack: `${name} ${handler} ${logicText(a as unknown as ItemChange)}`.toLowerCase(),
      searchKind: 'Buttons' as SearchKind,
    }
  })
}

function buttonDetail(d: ReturnType<typeof legacyActionToDef>): string {
  const handler = ACTION_HANDLER_LABELS[d.handler] || d.handler
  const checks = (d.validations || []).length
  const bits = [handler]
  if (d.handler === 'system') bits[0] = `Built-in · ${d.system_op || 'add'}`
  else if (d.handler === 'report' && d.report?.obj_name) bits.push(d.report.obj_name)
  else if (d.handler === 'open' && d.open?.obj_name) bits.push(d.open.obj_name)
  else if (d.handler === 'wizard' && d.wizard?.obj_name) bits.push(d.wizard.obj_name)
  else if (d.handler === 'send') {
    const tpl = d.send?.default_template
    const ch = (d.send?.channels || []).length
    if (tpl) bits.push(tpl)
    if (ch) bits.push(plural(ch, 'channel'))
  }
  else if (d.business_logic?.method) bits.push(d.business_logic.method)
  if (checks) bits.push(plural(checks, 'check'))
  if (d.confirmation && d.confirmation.mode && d.confirmation.mode !== 'none') bits.push('asks first')
  return bits.join(' · ')
}

export function fieldEvents(model: TransactionModel, fi: number): SetupItem[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  const out: SetupItem[] = []
  ;(sm?.columns || []).forEach((w, ci) => {
    const c = w?.column
    if (!c || !configured(c.item_change)) return
    const k = logicKindOf(c.item_change)
    out.push({
      id: `fev:${fi}:${ci}`, name: `When ${colLabel(c)} changes`, detail: `${k.label} · ${logicDetail(c.item_change)}`,
      kind: k.kind, scopeKey: `form:${fi}`, scopeLabel: title,
      node: { type: 'column', formIndex: fi, columnIndex: ci, label: colLabel(c), tab: 'behaviour' },
      haystack: `${colLabel(c)} ${c.heading || ''} ${logicText(c.item_change)}`.toLowerCase(),
      searchKind: 'Events',
    })
  })
  return out
}

const ROW_EVENT_NAMES = {
  row_add: 'When a row is added',
  row_edit: 'When a row is opened',
  row_complete: 'When the row is finished',
} as const

export function rowEvents(model: TransactionModel, fi: number, which?: Array<keyof typeof ROW_EVENT_NAMES>): SetupItem[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  const keys = which || (['row_add', 'row_edit', 'row_complete'] as const)
  return keys.filter(k => configured(sm?.[k])).map(k => {
    const raw = sm?.[k] as ItemChange | string
    const kk = logicKindOf(raw)
    return {
      id: `rev:${fi}:${k}`, name: ROW_EVENT_NAMES[k], detail: `${kk.label} · ${logicDetail(raw)}`,
      kind: kk.kind, scopeKey: `form:${fi}`, scopeLabel: title,
      node: { type: 'form-row-events', formIndex: fi, label: 'Row events' },
      haystack: `${ROW_EVENT_NAMES[k]} ${k} ${logicText(raw)}`.toLowerCase(),
      searchKind: 'Events' as SearchKind,
    }
  })
}

export function formValidations(model: TransactionModel, fi: number): SetupItem[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  return (Array.isArray(sm?.validations) ? sm!.validations : []).map((v, vi) => ({
    id: `fval:${fi}:${vi}`, name: validationName(v), detail: `${severityLabel(v)} · on the form`,
    kind: validationKind(v), scopeKey: `form:${fi}`, scopeLabel: title,
    node: { type: 'form-validations', formIndex: fi, label: 'Validations' },
    haystack: validationText(v), searchKind: 'Events' as SearchKind,
  }))
}

export function columnValidations(model: TransactionModel, fi: number): SetupItem[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  const out: SetupItem[] = []
  ;(sm?.columns || []).forEach((w, ci) => {
    const c = w?.column
    const list = Array.isArray(c?.validations) ? c!.validations : []
    list.forEach((v, vi) => out.push({
      id: `cval:${fi}:${ci}:${vi}`, name: validationName(v), detail: `${severityLabel(v)} · on ${colLabel(c!)}`,
      kind: validationKind(v), scopeKey: `form:${fi}`, scopeLabel: title,
      node: { type: 'column', formIndex: fi, columnIndex: ci, label: colLabel(c!), tab: 'rules' },
      haystack: `${colLabel(c!)} ${validationText(v)}`, searchKind: 'Events',
    }))
  })
  return out
}

export function formJoins(model: TransactionModel, fi: number): SetupItem[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  return (sm?.joins?.join_predicates?.joins || []).map((j, ji) => ({
    id: `join:${fi}:${ji}`, name: j.table || `Table ${ji + 1}`,
    detail: j.main_table ? 'Main table' : `${(j.join_type || 'inner').toUpperCase()} join`,
    kind: 'Rule' as LogicKind, scopeKey: `form:${fi}`, scopeLabel: title, mono: true,
    node: { type: 'form', formIndex: fi, label: title, tab: 'data' },
    haystack: JSON.stringify(j).toLowerCase(), searchKind: 'Events' as SearchKind,
  }))
}

function hookItems(
  list: (ItemChange | string)[] | undefined, idPrefix: string, noun: string,
  scopeKey: string, scopeLabel: string, node: TreeNode,
): SetupItem[] {
  return (list || []).map((raw, i) => {
    const k = logicKindOf(raw)
    const detail = logicDetail(raw)
    return {
      id: `${idPrefix}:${i}`, name: detail && detail !== 'Function not named yet' ? detail : `${noun} ${i + 1}`,
      detail: `${noun} · ${k.label}`, kind: k.kind, scopeKey, scopeLabel, node, mono: k.kind === 'Code',
      haystack: `${noun} ${logicText(raw)}`.toLowerCase(), searchKind: 'Events' as SearchKind,
    }
  })
}

export function formCrossUpdates(model: TransactionModel, fi: number): SetupItem[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  return hookItems(sm?.cross_updates, `fcross:${fi}`, 'Cross update', `form:${fi}`, title,
    { type: 'form-cross-updates', formIndex: fi, label: 'Cross updates' })
}

/** Line-entry sources + lookup "Add …" columns on a child form. */
export function lineEntryCount(sm: SqlModel | undefined): number {
  if (!sm) return 0
  return (sm.line_entry?.sources?.length || 0) + (sm.columns || []).filter(w => {
    const v = w?.column?.lookup_add_lines
    return truthy(v) && !!(w?.column?.lookup || '').trim()
  }).length
}

/** The "<form> ▾" menu and the "Set up on <form>" list — same categories. */
export function formCategories(model: TransactionModel, fi: number): SetupCategory[] {
  const sm = model.sql_models[fi]?.sql_model
  const title = formTitle(sm, fi)
  const isHeader = (allSqlModels(model)[fi]?.formNo ?? '') === headerFormNo(model)
  const buttons = formButtons(model, fi)
  const fev = fieldEvents(model, fi)
  const rev = rowEvents(model, fi)
  const fval = formValidations(model, fi)
  const cval = columnValidations(model, fi)
  const joins = formJoins(model, fi)
  const cross = formCrossUpdates(model, fi)
  const cats: SetupCategory[] = [
    { key: 'fbuttons', title: 'Form buttons', count: String(buttons.length), items: buttons,
      node: { type: 'form-actions', formIndex: fi, label: 'Form buttons' } },
    { key: 'fevents', title: 'Field events', count: String(fev.length), items: fev,
      node: { type: 'form-field-events', formIndex: fi, label: 'Field events' } },
    { key: 'revents', title: 'Row events', count: String(rev.length), items: rev,
      node: { type: 'form-row-events', formIndex: fi, label: 'Row events' } },
    { key: 'fvalid', title: 'Validations',
      count: cval.length ? `${fval.length} + ${cval.length} on columns` : String(fval.length), items: fval,
      node: { type: 'form-validations', formIndex: fi, label: 'Validations' } },
    { key: 'joins', title: 'Joins', count: plural(joins.length, 'table'), items: joins,
      node: { type: 'form', formIndex: fi, label: title, tab: 'data' } },
    { key: 'fcross', title: 'Cross updates', count: String(cross.length), items: cross,
      node: { type: 'form-cross-updates', formIndex: fi, label: 'Cross updates' } },
  ]
  if (!isHeader) {
    const n = lineEntryCount(sm)
    cats.push({ key: 'lineentry', title: 'Line entry', count: plural(n, 'source'), items: [],
      node: { type: 'form-line-entry', formIndex: fi, label: 'How lines are filled' } })
  }
  return cats
}

// ── whole record ───────────────────────────────────────────────────────────

export function recordButtons(model: TransactionModel): SetupItem[] {
  return (model.actions || []).map((a, i) => {
    const d = legacyActionToDef(a)
    const name = (d.name || '').trim() || 'Unnamed button'
    return {
      id: `rbtn:${i}`, name, detail: buttonDetail(d), kind: 'Button' as LogicKind,
      scopeKey: 'record', scopeLabel: 'Whole record',
      node: { type: 'object-actions', actionIndex: i, label: name },
      haystack: `${name} ${logicText(a as unknown as ItemChange)}`.toLowerCase(),
      searchKind: 'Buttons' as SearchKind,
    }
  })
}

export function followUps(model: TransactionModel): SetupItem[] {
  return (model.follow_up_actions || []).map((a, i) => {
    const d = normalizeFollowupAction(a)
    const target = d.send
      ? `send message${d.send.template ? ` (${d.send.template})` : ''}`
      : d.ioflow_event?.event_name
        ? `IOFlow event ${d.ioflow_event.event_name}`
        : d.business_logic?.method || d.rest_api?.url || ''
    return {
      id: `fup:${i}`, name: (d.name || '').trim() || target || `Follow-up ${i + 1}`,
      detail: `Follow-up · ${followupEventLabel(d.event)}${target ? ` → ${target}` : ''}`,
      kind: followupKind(d), scopeKey: 'record', scopeLabel: 'Whole record', mono: true,
      node: { type: 'followup-actions', label: 'Follow-ups' },
      haystack: logicText(a as unknown as ItemChange), searchKind: 'Events' as SearchKind,
    }
  })
}

export function preCommit(model: TransactionModel): SetupItem[] {
  return hookItems(model.pre_commit, 'pre', 'Before saving', 'record', 'Whole record',
    { type: 'pre-commit', label: 'Before saving (pre-commit)' })
}
export function postCommit(model: TransactionModel): SetupItem[] {
  return hookItems(model.post_commit, 'post', 'After save', 'record', 'Whole record',
    { type: 'transaction-complete', label: 'After save (post-commit)' })
}
export function legacyCrossUpdates(model: TransactionModel): SetupItem[] {
  return hookItems(model.cross_updates, 'xcross', 'Cross update (legacy, record level)', 'record', 'Whole record',
    { type: 'cross-updates', label: 'Cross updates (legacy, record level)' })
}

export function approvalSummary(model: TransactionModel): { on: boolean; text: string } {
  const ap = model.approval
  if (!ap || !ap.enabled) return { on: false, text: 'Off' }
  const n = ap.levels?.length || 0
  return { on: true, text: `On · ${plural(n, 'level')}` }
}

export interface SettingsSection {
  key: SettingsKey
  label: string
  detail: string
  state: string
  on: boolean
  node: TreeNode
}
export type SettingsKey =
  | 'general' | 'approvals' | 'work-rules' | 'website' | 'offline'
  | 'assistant-authoring' | 'vector-search' | 'file-import' | 'attachments'
  | 'additional-attributes'

/** The Settings workspace's sections, with their state pills. */
export function settingsSections(model: TransactionModel): SettingsSection[] {
  const ap = approvalSummary(model)
  const wr = ((model as { work_rules?: unknown[] }).work_rules || []).length
  const contentOn = !!model.content && model.content.enabled !== false
  const off = model.offline
  const au = model.authoring
  const auOn = au ? au.enabled !== false : !!model.content?.enabled
  const vs = model.vector_search
  const docs = (model.attach_docs || []).length
  const fi = model.import_settings
  const fiOn = !!(fi && (fi.sender_field || Object.values(fi.instructions || {}).some((v) => (v || '').trim())
    || fi.learn_profiles === false))
  const udaSets = Object.values(model.uda?.forms || {})
    .reduce((n, f) => n + ((f?.sets || []).length), 0)
  return [
    { key: 'general', label: 'General', detail: 'Object name, counts', state: '', on: false,
      node: { type: 'object', label: model.obj_name } },
    { key: 'approvals', label: 'Approvals',
      detail: ap.on ? `${plural(model.approval?.levels?.length || 0, 'level')} · ${model.approval?.profile === 'advanced' ? 'Advanced' : 'Simple'}` : 'Sign-off before a save or a button',
      state: ap.on ? 'On' : 'Off', on: ap.on, node: { type: 'approvals', label: 'Approvals' } },
    { key: 'work-rules', label: 'Work rules', detail: 'Tasks raised from records',
      state: wr ? plural(wr, 'rule') : 'None', on: wr > 0, node: { type: 'work-rules', label: 'Work rules' } },
    { key: 'website', label: 'Publish on website', detail: 'Entry-screen panel, checks',
      state: contentOn ? 'On' : 'Off', on: contentOn, node: { type: 'website', label: 'Publish on website' } },
    { key: 'offline', label: 'Offline & sync', detail: 'Push / pull, conflicts',
      state: off?.eligible ? 'On' : 'Off', on: !!off?.eligible, node: { type: 'offline', label: 'Offline & sync' } },
    { key: 'assistant-authoring', label: 'Assistant authoring', detail: 'Drafts content for records',
      state: auOn ? 'On' : 'Off', on: auOn, node: { type: 'assistant-authoring', label: 'Assistant authoring' } },
    { key: 'vector-search', label: 'Search by meaning', detail: 'Vector search columns',
      state: vs?.enabled ? plural((vs.columns || []).length, 'col') : 'Off', on: !!vs?.enabled,
      node: { type: 'vector-search', label: 'Search by meaning' } },
    { key: 'file-import', label: 'File import', detail: 'Sender, reading instructions',
      state: fiOn ? 'Set' : 'Default', on: fiOn,
      node: { type: 'file-import', label: 'File import' } },
    { key: 'attachments', label: 'Document attachments', detail: 'Allowed document types',
      state: docs ? String(docs) : 'None', on: docs > 0, node: { type: 'attachments', label: 'Document attachments' } },
    { key: 'additional-attributes', label: 'Additional attributes',
      detail: 'Extra details by customer, item…', state: udaSets ? plural(udaSets, 'set') : 'None',
      on: udaSets > 0, node: { type: 'additional-attributes', label: 'Additional attributes' } },
  ]
}

/** TreeNode type → Settings section (the inspector routes these to Settings). */
export const SETTINGS_NODE_TYPES: Partial<Record<TreeNode['type'], SettingsKey>> = {
  object: 'general', approvals: 'approvals', 'work-rules': 'work-rules', website: 'website',
  offline: 'offline', 'assistant-authoring': 'assistant-authoring', 'vector-search': 'vector-search',
  'file-import': 'file-import', attachments: 'attachments',
  'additional-attributes': 'additional-attributes',
}

/** The "Whole record ▾" menu and the "<object> · whole record" list. */
export function recordCategories(model: TransactionModel): SetupCategory[] {
  const rb = recordButtons(model)
  const fu = followUps(model)
  const saving = [...preCommit(model), ...postCommit(model), ...legacyCrossUpdates(model)]
  const ap = approvalSummary(model)
  const sections = settingsSections(model).filter(s => !['general', 'approvals'].includes(s.key))
  const settingsOn = sections.filter(s => s.on).length
  return [
    { key: 'rbuttons', title: 'Record buttons', count: String(rb.length), items: rb,
      node: { type: 'object-actions', label: 'Record buttons' } },
    { key: 'followups', title: 'Follow-ups', count: String(fu.length), items: fu,
      node: { type: 'followup-actions', label: 'Follow-ups' } },
    { key: 'saving', title: 'When saving', count: String(saving.length), items: saving,
      node: { type: 'record-saving', label: 'Before / after saving' } },
    { key: 'approvals', title: 'Approvals', count: ap.text,
      items: ap.on ? [{
        id: 'approval', name: approvalChainText(model), detail: ap.text, kind: 'Gate', scopeKey: 'record',
        scopeLabel: 'Whole record', node: { type: 'approvals', label: 'Approvals' },
        haystack: JSON.stringify(model.approval || {}).toLowerCase(), searchKind: 'Events',
      }] : [],
      node: { type: 'approvals', label: 'Approvals' } },
    { key: 'rsettings', title: 'Record settings', count: `${settingsOn} on`,
      items: sections.map(s => ({
        id: `set:${s.key}`, name: s.label, detail: s.state, kind: 'Rule' as LogicKind,
        scopeKey: 'record', scopeLabel: 'Whole record', node: s.node, haystack: s.label.toLowerCase(),
        searchKind: 'Events' as SearchKind,
      })),
      node: { type: 'record-settings', label: 'Record settings' } },
  ]
}

export function approvalChainText(model: TransactionModel): string {
  const levels = model.approval?.levels || []
  const roles = levels.map(l => (l.name || l.approver_role || '').trim()).filter(Boolean)
  if (!roles.length) return levels.length ? plural(levels.length, 'level') : 'No levels yet'
  return roles.length === 1 ? roles[0] : `${roles.slice(0, -1).join(', ')}, then ${roles[roles.length - 1]}`
}

// ── Logic timeline ─────────────────────────────────────────────────────────

export interface LogicStage {
  n: number
  key: 'opened' | 'field' | 'finished' | 'button' | 'before' | 'approval' | 'after'
  title: string
  sub: string
  items: SetupItem[]
}

export function logicStages(model: TransactionModel): LogicStage[] {
  const forms = allSqlModels(model).map((_, fi) => fi)
  const flat = <T,>(f: (fi: number) => T[]) => forms.flatMap(f)
  const ap = approvalSummary(model)
  return [
    { n: 1, key: 'opened', title: 'A row is added or opened', sub: 'Row add · row edit events',
      items: flat(fi => rowEvents(model, fi, ['row_add', 'row_edit'])) },
    { n: 2, key: 'field', title: 'A field changes', sub: 'When it changes — per column',
      items: flat(fi => fieldEvents(model, fi)) },
    { n: 3, key: 'finished', title: 'A row is finished', sub: 'On row complete',
      items: flat(fi => rowEvents(model, fi, ['row_complete'])) },
    { n: 4, key: 'button', title: 'Someone clicks a button', sub: 'Buttons, their checks and confirmation',
      items: [...recordButtons(model), ...flat(fi => formButtons(model, fi))] },
    { n: 5, key: 'before', title: 'Before saving', sub: 'Validations, cross updates, pre-commit — all can block',
      items: [
        ...flat(fi => columnValidations(model, fi)),
        ...flat(fi => formValidations(model, fi)),
        ...flat(fi => formCrossUpdates(model, fi)),
        ...preCommit(model),
      ] },
    { n: 6, key: 'approval', title: 'Approval', sub: 'The record waits here until approved',
      items: ap.on ? [{
        id: 'approval', name: approvalChainText(model), detail: ap.text, kind: 'Gate', scopeKey: 'record',
        scopeLabel: 'Whole record', node: { type: 'approvals', label: 'Approvals' },
        haystack: '', searchKind: 'Events',
      }] : [] },
    { n: 7, key: 'after', title: 'After saving', sub: 'Post-commit, follow-ups — never block the save',
      items: [...postCommit(model), ...followUps(model), ...legacyCrossUpdates(model)] },
  ]
}

// ── search ─────────────────────────────────────────────────────────────────

export interface SearchResult {
  id: string
  kind: SearchKind
  name: string
  where: string
  mono: boolean
  node: TreeNode
  haystack: string
}

export function searchIndex(model: TransactionModel): SearchResult[] {
  const out: SearchResult[] = []
  const entries = allSqlModels(model)
  entries.forEach(({ sm }, fi) => {
    const title = formTitle(sm, fi)
    ;(sm.columns || []).forEach((w, ci) => {
      const c = w?.column
      if (!c) return
      const bits = [`${title} › ${c.group_name || 'Others'}`, (c.col_type || '').toUpperCase()]
      if (truthy(c.mandatory)) bits.push('mandatory')
      if (configured(c.item_change)) bits.push('fills other fields')
      out.push({
        id: `col:${fi}:${ci}`, kind: 'Columns', name: colLabel(c), where: bits.filter(Boolean).join(' · '),
        mono: true, node: { type: 'column', formIndex: fi, columnIndex: ci, label: colLabel(c), tab: 'general' },
        haystack: [c.name, c.db_name, c.heading, c.descr, c.group_name, c.table_name, c.lookup, c.lookup_sql,
          c.sql_expression, c.default_value].filter(Boolean).join(' ').toLowerCase(),
      })
    })
  })
  const events: SetupItem[] = []
  entries.forEach((_, fi) => {
    events.push(...fieldEvents(model, fi), ...rowEvents(model, fi), ...columnValidations(model, fi),
      ...formValidations(model, fi), ...formCrossUpdates(model, fi), ...formJoins(model, fi))
  })
  events.push(...preCommit(model), ...postCommit(model), ...followUps(model), ...legacyCrossUpdates(model))
  for (const e of events) {
    out.push({ id: e.id, kind: 'Events', name: e.name, where: `${e.scopeLabel} · ${e.detail}`,
      mono: !!e.mono, node: e.node, haystack: `${e.name} ${e.haystack}`.toLowerCase() })
  }
  const buttons = [...recordButtons(model), ...entries.flatMap((_, fi) => formButtons(model, fi))]
  for (const b of buttons) {
    out.push({ id: b.id, kind: 'Buttons', name: b.name, where: `${b.scopeLabel} · ${b.detail}`,
      mono: false, node: b.node, haystack: `${b.name} ${b.haystack}`.toLowerCase() })
  }
  return out
}

/** Header form + its direct children, as tabs on the record. */
export function recordTabs(model: TransactionModel): Array<{ fi: number; title: string; nested: number }> {
  const entries = allSqlModels(model)
  const headerNo = headerFormNo(model)
  const idx = new Map(entries.map((e, i) => [e.formNo, i]))
  const countNested = (formNo: string, seen = new Set<string>()): number => {
    let n = 0
    for (const k of childFormsOf(model, formNo)) {
      if (seen.has(k.formNo)) continue
      seen.add(k.formNo)
      n += 1 + countNested(k.formNo, seen)
    }
    return n
  }
  const tabs = [{ fi: idx.get(headerNo) ?? 0, title: formTitle(entries[idx.get(headerNo) ?? 0]?.sm, 0), nested: 0 }]
  for (const k of childFormsOf(model, headerNo)) {
    const fi = idx.get(k.formNo)
    if (fi === undefined) continue
    tabs.push({ fi, title: formTitle(k.sm, fi), nested: countNested(k.formNo) })
  }
  // A form whose parent chain never reaches the header still needs a tab.
  const reach = new Set<number>()
  const walk = (formNo: string) => {
    const fi = idx.get(formNo)
    if (fi === undefined || reach.has(fi)) return
    reach.add(fi)
    for (const k of childFormsOf(model, formNo)) walk(k.formNo)
  }
  walk(headerNo)
  entries.forEach((e, fi) => { if (!reach.has(fi)) tabs.push({ fi, title: formTitle(e.sm, fi), nested: 0 }) })
  return tabs
}

/** The record tab a form sits under (itself for the header / a direct child). */
export function topTabOf(model: TransactionModel, fi: number): number {
  const entries = allSqlModels(model)
  const headerNo = headerFormNo(model)
  const idx = new Map(entries.map((e, i) => [e.formNo, i]))
  let cur = entries[fi]
  const seen = new Set<string>()
  while (cur && !seen.has(cur.formNo)) {
    seen.add(cur.formNo)
    const parent = String(cur.sm.parent_form_no ?? '').trim()
    if (!parent || parent === cur.formNo || parent === headerNo || cur.formNo === headerNo) {
      return idx.get(cur.formNo) ?? fi
    }
    const p = idx.get(parent)
    if (p === undefined) return idx.get(cur.formNo) ?? fi
    cur = entries[p]
  }
  return fi
}

/** A tab's own form plus every form nested under it, in tree order. */
export function nestedChain(model: TransactionModel, tabFi: number): Array<{ fi: number; title: string; depth: number }> {
  const entries = allSqlModels(model)
  const idx = new Map(entries.map((e, i) => [e.formNo, i]))
  const out: Array<{ fi: number; title: string; depth: number }> = []
  const seen = new Set<string>()
  const walk = (formNo: string, depth: number) => {
    if (seen.has(formNo)) return
    seen.add(formNo)
    const fi = idx.get(formNo)
    if (fi === undefined) return
    out.push({ fi, title: formTitle(entries[fi].sm, fi), depth })
    for (const k of childFormsOf(model, formNo)) walk(k.formNo, depth + 1)
  }
  const start = entries[tabFi]
  // The header's direct children are record tabs of their own, so the
  // header's chain is just itself.
  if (start && start.formNo === headerFormNo(model)) return [{ fi: tabFi, title: formTitle(start.sm, tabFi), depth: 0 }]
  if (start) walk(start.formNo, 0)
  return out
}

/** A form's main table: `main_table`, else the joins' main table, else its
 *  first column's table. */
export function mainTableOf(sm: SqlModel | undefined): string {
  if (!sm) return ''
  const own = (sm.main_table || '').trim()
  if (own) return own
  const j = (sm.joins?.join_predicates?.joins || []).find(x => x.main_table)
  if (j?.table) return j.table
  return (sm.columns || []).map(w => (w?.column?.table_name || '').trim()).find(Boolean) || ''
}

/** Count of every logic item — the Logic workspace badge. */
export function logicCount(model: TransactionModel): number {
  return logicStages(model).reduce((n, s) => n + s.items.length, 0)
}

// ── groups ─────────────────────────────────────────────────────────────────

/** A form's columns grouped as the canvas draws them: group order follows the
 *  first column's y (then first appearance), Basic Information pinned first
 *  and Others last; columns inside a group sort by y then declaration order. */
export function groupColumns(sm: SqlModel | undefined): Array<[string, Array<{ col: ColumnDef; index: number }>]> {
  const map = new Map<string, { col: ColumnDef; index: number }[]>()
  const firstAppearance = new Map<string, number>()
  ;(sm?.columns || []).forEach((w, i) => {
    const col = w?.column
    if (!col) return
    const group = col.group_name || 'Others'
    if (!map.has(group)) {
      map.set(group, [])
      firstAppearance.set(group, i)
    }
    map.get(group)!.push({ col, index: i })
  })
  map.forEach(cols => {
    cols.sort((a, b) => {
      const dy = (Number(a.col.y) || 0) - (Number(b.col.y) || 0)
      return dy !== 0 ? dy : a.index - b.index
    })
  })
  const groupPos = new Map<string, { y: number; idx: number }>()
  map.forEach((cols, name) => {
    groupPos.set(name, { y: Number(cols[0]?.col.y) || 0, idx: firstAppearance.get(name) ?? 0 })
  })
  const entries = [...map.entries()]
  entries.sort((a, b) => {
    if (a[0] === 'Basic Information' || a[0] === 'Basic') return -1
    if (b[0] === 'Basic Information' || b[0] === 'Basic') return 1
    if (a[0] === 'Others' || a[0] === 'Other Information') return 1
    if (b[0] === 'Others' || b[0] === 'Other Information') return -1
    const pa = groupPos.get(a[0])!
    const pb = groupPos.get(b[0])!
    if (pa.y !== pb.y) return pa.y - pb.y
    return pa.idx - pb.idx
  })
  return entries
}

/** The caption a group shows: its group_settings label, else its name. */
export function groupLabel(sm: SqlModel | undefined, name: string): string {
  const key = name === 'Others' ? '' : name
  const lbl = (sm?.group_settings?.[key]?.label || sm?.group_settings?.[name]?.label || '').trim()
  if (lbl) return lbl
  return name === 'Others' ? 'Other Information' : (name || 'Other Information')
}

/** How many pieces of logic hang off a column (the ƒ n badge). */
export function columnLogicCount(c: ColumnDef | undefined): number {
  if (!c) return 0
  let n = normalizeItemChange(c.item_change) ? 1 : 0
  n += Array.isArray(c.validations) ? c.validations.length : 0
  if ((c.lookup || '').trim() || (c.lookup_sql || '').trim()) n += 1
  return n
}
